import { Suspense, useCallback, useEffect, useMemo, useState, type ChangeEvent, type ReactElement } from 'react'

import { ArrowUpward as ArrowUpwardIcon, Close as CloseIcon, ViewInAr as ViewInArIcon } from '@mui/icons-material'
import { Alert, AlertTitle, Box, Button, Checkbox, Collapse, Fade, FormControlLabel, InputLabel, MenuItem, NoSsr, Stack, Typography, styled } from '@mui/material'
import Dinero from 'dinero.js'
import Immutable from 'immutable'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import type ProductOptionStore from '../../../AppData/_stores/ProductOptionStore'
import AddToCartButton from '../../../Cart/AddToCartButton'
import ErrorBoundary from '../../../Common/_components/ErrorBoundary'
import { useAppDispatch } from '../../../Common/_hooks/useAppDispatch'
import { useAppSelector } from '../../../Common/_hooks/useAppSelector'
import useDocumentSize from '../../../Common/_hooks/useDocumentSize'
import { appendMetaEventData, getMetaStandaloneEventId, trackMetaEvent } from '../../../Common/Tracking/MetaConversions'
import { trackOpenAIAdsItemsAdded } from '../../../Common/Tracking/OpenAIAdsTracking'
import NumberFieldV2 from '../../../Common/Form/NumberFieldV2'
import ColorField from '../../../Common/Form/ColorField'
import SelectV2 from '../../../Common/Form/SelectV2'
import LabLoader from '../../../DesignLab/_components/LabLoader'
import FabricViewer from '../../../DesignLab/FabricLab/_components/FabricViewer'
import ModelViewer from '../../../ModelViewer/ModelViewer'
import { GetProductLabData } from '../../../UIData/_actions/DesignLabActions'
import { FetchBrandingOptions } from '../../../UserData/_actions/BrandingOptionActions'
import { UpdateCartItem } from '../../../UserData/_actions/UserCartActions'
import { FetchOneItem } from '../../../UserData/_actions/UserDataActions'
import { type ProductItem } from '../../../UserData/_stores/UserCartStore'
import ProductBrandingOption from '../ProductBrandingOption'
import ProductGpsrTab from './ProductGpsrTab'
import OrderDialogTabsHeader from './OrderDialogTabsHeader'
import ProductMaterialsTab from './ProductMaterialsTab'
import ProductOptionTypeStore from '../../../AppData/_stores/ProductOptionTypeStore'

const MOBILE_BREAKPOINT = 760
const DESKTOP_DIALOG_MARGIN = 64
const MOBILE_DIALOG_MARGIN = 32
const DESKTOP_CONTENT_PADDING = 48
const MOBILE_CONTENT_PADDING = 32
const DESKTOP_CONTENT_GAP = 20
const DESKTOP_DETAILS_WIDTH = 290
const THUMBNAIL_LIST_HEIGHT = 105
const PREVIEW_THUMBNAIL_GAP = 15
const DESKTOP_BODY_CHROME_HEIGHT = 150
const MOBILE_BODY_CHROME_HEIGHT = 128
const MIN_DIALOG_BODY_HEIGHT = 260
const MIN_DIALOG_BODY_WIDTH = 280
const MAX_PREVIEW_WIDTH = 560
const PREVIEW_RATIO = 500 / 560

type OrderDialogTab = 'order' | 'materials' | 'gpsr'

interface OrderDialogTabPanelProps {
  children: ReactElement
  selectedTab: OrderDialogTab
  tab: OrderDialogTab
}

const OrderDialogTabsBody = styled('div')({
  display: 'grid',
  alignItems: 'stretch',
  overflow: 'hidden',
  position: 'relative'
})

const OrderDialogTabPanelRoot = styled('div')({
  gridArea: '1 / 1',
  height: '100%',
  minWidth: 0,
  minHeight: 0,
  overflow: 'hidden',

  '&.order': {
    overflow: 'hidden'
  },

  '&.info': {
    overflowX: 'hidden',
    overflowY: 'auto',
    scrollbarGutter: 'stable'
  },

  '&.info > div': {
    boxSizing: 'border-box',
    maxHeight: '100%',
    maxWidth: '100%',
    width: '100%'
  },

  '&.inactive': {
    pointerEvents: 'none',
    visibility: 'hidden'
  },

  '&.info.inactive': {
    position: 'absolute',
    inset: 0,
  }
})

function OrderDialogTabPanel(props: OrderDialogTabPanelProps) {
  const isSelected = props.selectedTab === props.tab
  const className = ((props.tab === 'order' ? 'order ' : 'info ') + (!isSelected ? 'inactive' : '')).trim()

  return <OrderDialogTabPanelRoot
    aria-hidden={!isSelected}
    className={className !== '' ? className : undefined}
    role="tabpanel"
  >
    {props.children}
  </OrderDialogTabPanelRoot>
}

const StyledDialogContent = styled('div')(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing(3),
  boxSizing: 'border-box',
  height: '100%',
  overflow: 'hidden',
  gap: '20px',

  ['@media (max-width: ' + MOBILE_BREAKPOINT + 'px)']: {
    flexDirection: 'column',
    overflow: 'auto',
    gap: '30px',
    padding: theme.spacing(2)
  },

  '&::-webkit-scrollbar': {
    width: '10px',
    height: '10px'
  },

  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent'
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    borderRadius: '10px',

    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.25)'
    }
  }
}))

const PreviewListWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 15,

  ['@media (max-width: ' + MOBILE_BREAKPOINT + 'px)']: {
    order: 2
  }
})

const PreviewWrapper = styled('div')({
  backgroundColor: '#f3f3f3',
  overflow: 'auto',

  '&.is-3d': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },

  '&::-webkit-scrollbar': {
    width: '10px',
    height: '10px'
  },

  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent'
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    borderRadius: '10px',

    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.25)'
    }
  }
})

const ThumbnailList = styled('div')({
  display: 'flex',
  gap: 5,
  overflow: 'auto',
  paddingBottom: '5px',

  '&::-webkit-scrollbar': {
    width: '10px',
    height: '10px'
  },

  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent'
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    borderRadius: '10px',

    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.25)'
    }
  }
})

const Thumbnail = styled('div')({
  border: '2px solid transparent',
  overflow: 'hidden',
  cursor: 'pointer',
  borderRadius: 15,
  position: 'relative',
  flexShrink: 0,

  '&.active': {
    borderColor: '#333'
  }
})

const ThumbnailImg = styled('img')({
  height: '90px',
  display: 'block'
})

const ThumbnailIconWrapper = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
})

const FabricViewerWrapper = styled('div')({
  paddingBottom: '30px',
  display: 'flex'
})

const DetailsWrapper = styled('div')(({ theme }) => ({
  flexDirection: 'column',
  display: 'flex',
  padding: '0 10px 10px 10px',
  boxSizing: 'border-box',
  width: 290,
  flexShrink: 0,
  overflow: 'auto',
  justifyContent: 'safe center',

  '&::-webkit-scrollbar': {
    width: '10px',
    height: '10px'
  },

  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent'
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    borderRadius: '10px',

    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.25)'
    }
  },

  ['@media (max-width: ' + MOBILE_BREAKPOINT + 'px)']: {
    width: '100%',
    order: 1,
    margin: '0 -10px',
  }
}))

const Price = styled('div')(({ theme }) => ({
  fontSize: '22px',
  fontWeight: '600',
  color: theme.palette.accent.main
}))

const WholesaleNotice = styled('div')(({ theme }) => ({
  fontSize: '14px',
  marginTop: '5px',
  color: theme.palette.accent.main
}))

interface Props {
  productDesignId?: number
  item?: ProductItem
  onConfirm?: (listOptions: Immutable.Map<number, number>, listBrandingOptions: Immutable.Map<number, number>) => void
  showTabs?: boolean
  selectedOptions?: Array<[number, number]>
  selectedBrandingOptions?: Array<[number, number]>
  cartLinkReloadDocument?: boolean
  opened: boolean
  onClose: () => void
}

interface OrderDialogOrderPanelProps extends OrderDialogTabPanelProps {
  showTabs: boolean
}

function OrderDialogOrderPanel(props: OrderDialogOrderPanelProps) {
  const { showTabs, ...panelProps } = props

  if (!showTabs) {
    return props.children
  }

  return <OrderDialogTabPanel {...panelProps} />
}

const isOptionDependencySatisfied = (option: ProductOptionStore, listSelectedOptions: Immutable.Map<number, number>) => {
  if (option.get('listDependencies').count() === 0) {
    return true
  }

  let isValid = false
  option.get('listDependencies').forEach(dependency => {
    listSelectedOptions.forEach(optionId => {
      if (optionId === dependency) {
        isValid = true
      }
    })
  })

  return isValid
}

const getAvailableOptions = (optionType: ProductOptionTypeStore, listSelectedOptions: Immutable.Map<number, number>) => {
  return optionType.get('options')
    .sort((a, b) => a.get('theorder') - b.get('theorder'))
    .valueSeq()
    .filter(option => isOptionDependencySatisfied(option, listSelectedOptions))
    .toArray()
}

export default function OrderDialogContent(props: Props) {
  const dispatch = useAppDispatch()
  const [t] = useTranslation('account-v2')
  const [tR] = useTranslation('routes')
  const showTabs = props.showTabs !== false

  const userCurrency = useAppSelector(state => state.get('userData').get('prefs').get('currency'))
  const productDesign = useAppSelector(state => props.productDesignId ? state.get('userData').get('productDesigns').get('data').get(String(props.productDesignId)) : undefined)
  const productData = useAppSelector(state => state.get('appData').get('products').get(String(productDesign?.get('id_product'))))
  const brandingOptions = useAppSelector(state => state.get('userData').get('brandingOptions'))

  const [selectedTab, setSelectedTab] = useState<OrderDialogTab>('order')
  const [selectedPreview, setSelectedPreview] = useState(0)
  const [texture, setTexture] = useState<HTMLImageElement | null>(null)
  const [quantity, setQuantity] = useState(1)
  const [listSelectedOptions, setListSelectedOptions] = useState(Immutable.Map<number, number>(props.selectedOptions ?? []))
  const [listSelectedBrandingOptions, setListSelectedBrandingOptions] = useState(Immutable.Map<number, number>(props.selectedBrandingOptions ?? []))
  const [addedToCart, setAddedToCart] = useState(false)
  const [error, setError] = useState(false)

  const documentSize = useDocumentSize()
  const isMobile = documentSize.width <= MOBILE_BREAKPOINT

  useEffect(() => {
    dispatch(FetchBrandingOptions())
  }, [])

  useEffect(() => {
    if (props.opened) {
      setSelectedTab('order')
    }
  }, [props.opened, props.productDesignId])

  useEffect(() => {
    if (!props.productDesignId || !props.opened) return

    setQuantity(1)
    setAddedToCart(false)
    dispatch(FetchOneItem(props.productDesignId, 'productDesigns').set({
      onFailure: () => {
        setError(true)
      }
    }))
  }, [props.opened, props.productDesignId])

  useEffect(() => {
    if (props.opened && productData && !productData.get('labData')) {
      dispatch(GetProductLabData(productData.get('slug')))
    }
  }, [props.opened, productData])

  useEffect(() => {
    if (!props.selectedOptions) return
    setListSelectedOptions(Immutable.Map<number, number>(props.selectedOptions))
  }, [props.selectedOptions])

  useEffect(() => {
    if (!props.selectedBrandingOptions) return
    setListSelectedBrandingOptions(Immutable.Map<number, number>(props.selectedBrandingOptions))
  }, [props.selectedBrandingOptions])

  useEffect(() => {
    if (!productDesign) return

    let tmpSelectedOptions = listSelectedOptions
    let tmpSelectedBrandingOptions = listSelectedBrandingOptions

    if (props.selectedOptions) {
      tmpSelectedOptions = Immutable.Map<number, number>(props.selectedOptions)
    }

    if (props.selectedBrandingOptions) {
      tmpSelectedBrandingOptions = Immutable.Map<number, number>(props.selectedBrandingOptions)
    }

    // Remove options that don't exist anymore
    listSelectedOptions.forEach((optionId, optionTypeId) => {
      const optionType = productDesign.get('availableOptionTypes').get(String(optionTypeId))
      if (!optionType?.get('options').has(String(optionId))) {
        tmpSelectedOptions = tmpSelectedOptions.remove(optionTypeId)
      }
    })

    // Autoselect design default options
    productDesign.get('defaultOptions').forEach((optionId, optionTypeId) => {
      const optionType = productDesign.get('availableOptionTypes').get(String(optionTypeId))

      if (!optionType?.get('options').has(String(optionId))) return

      // Only select default if we don't already have a value
      if (!tmpSelectedOptions.has(optionTypeId)) {
        tmpSelectedOptions = tmpSelectedOptions.set(optionTypeId, optionId)
      }
    })

    // Autoselect default options
    productDesign.get('availableOptionTypes').forEach(optionType => {
      let defaultOption: ProductOptionStore | undefined;

      optionType.get('options').forEach(option => {
        if (option.get('is_default')) {
          defaultOption = option;
          return false;
        }
      })

      // Only select default if we don't already have a value
      if (defaultOption !== undefined && !tmpSelectedOptions.has(optionType.get('id'))) {
        tmpSelectedOptions = tmpSelectedOptions.set(optionType.get('id'), defaultOption.get('id'))
      }
    })

    // Autoselect hidden options
    productDesign.get('availableOptionTypes').forEach(optionType => {
      if (optionType.get('visible')) {
        return
      }

      // There should only be 1 option so select first one
      const firstOption = optionType.get('options').first()
      if (!firstOption) return

      tmpSelectedOptions = tmpSelectedOptions.set(optionType.get('id'), firstOption.get('id'))
    })

    // Autoselect default branding options
    productDesign.get('availableOptionTypes').forEach(optionType => {
      // TODO: Hardcoded until we have a proper way to identify branding option types
      if (optionType.get('slug') !== 'include-label') {
        return
      }

      // There should only be 1 option so select first one
      const firstOption = optionType.get('options').first()
      if (!firstOption) return

      brandingOptions
        .filter(brandingOption => brandingOption.get('id_type') === 1)
        .forEach(brandingOption => {
          if (brandingOption.get('is_default')) {
            tmpSelectedOptions = tmpSelectedOptions.set(optionType.get('id'), firstOption.get('id'))
            tmpSelectedBrandingOptions = tmpSelectedBrandingOptions.set(firstOption.get('id'), brandingOption.get('id'))
          }
        })
    })

    // Autoselect item options if provided
    if (props.item) {
      props.item.get('options').forEach(option => {
        tmpSelectedOptions = tmpSelectedOptions.set(option.get('type_id'), option.get('id'))

        const idBrandingOption = option.get('id_branding_option')
        if (idBrandingOption) {
          tmpSelectedBrandingOptions = tmpSelectedBrandingOptions.set(option.get('id'), idBrandingOption)
        }
      })
      setQuantity(props.item.get('quantity'))
    }

    setListSelectedOptions(tmpSelectedOptions)
    setListSelectedBrandingOptions(tmpSelectedBrandingOptions)

    // Autoselect first preview
    if (productDesign.get('textures').count() > 0) {
      setSelectedPreview(0)
    } else {
      setSelectedPreview(productDesign.get('previews').first()?.get('id') ?? 0)
    }
  }, [productDesign, brandingOptions, props.item])

  const subproduct = useMemo(() => {
    if (!productDesign || !productData || productData.get('subproducts').count() === 0) return null;

    //TODO: This should support having subproducts based on multiple option types though none exist at the time of writing
    let listOptionSlugs: string[] = [];
    productData.get('subproducts').forEach(subproduct => {
      //Get selected option that matches the subproduct's option type
      const optionType = productDesign.get('availableOptionTypes').get(String(subproduct.get('id_product_option_type')))
      const optionSlug = optionType?.get('options').get(String(listSelectedOptions.get(subproduct.get('id_product_option_type'))))?.get('slug')

      if (optionSlug) {
        listOptionSlugs.push(optionSlug);
      }
    });

    return listOptionSlugs.join('|');
  }, [productDesign, productData, listSelectedOptions])

  const textureUrl = useMemo(() => {
    if (!productDesign || !productData) return undefined;

    //For subproducts, we find the one that matches the selected subproduct
    if (productData.get('has_subproducts')) {
      let tmpTextureUrl = undefined;
      productDesign.get('textures').forEach(texture => {
        if (texture.get('slug') === subproduct + '-texture') {
          tmpTextureUrl = texture.get('url');
          return false;
        }
      })
      return tmpTextureUrl;
    }

    return productDesign?.get('textures').first()?.get('url')
  }, [productDesign, productData, subproduct])

  // For subproducts (with LEGACY model viewer)
  const textureUrls = useMemo(() => {
    let tmpTextureUrls = ''

    let i = 0
    productDesign?.get('textures').forEach(texture => {
      if (i > 0) {
        tmpTextureUrls += ';'
      }

      tmpTextureUrls += texture.get('slug')+'|'+texture.get('url')
      i++
    })

    return tmpTextureUrls
  }, [productDesign])

  useEffect(() => {
    if (!textureUrl) {
      setTexture(null)
      return
    }

    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => {
      setTexture(image)
    }
    image.src = textureUrl
  }, [textureUrl])

  useEffect(() => {
    if (!productDesign) return

    let tmpSelectedOptions = listSelectedOptions
    listSelectedOptions.forEach((optionId, optionTypeId) => {
      const optionType = productDesign.get('availableOptionTypes').get(String(optionTypeId))
      if (!optionType) return

      const option = optionType.get('options').get(String(optionId))
      if (!option) return

      // Dependency check
      if (option.get('listDependencies').count() > 0) {
        let isValid = false
        option.get('listDependencies').forEach(dependency => {
          listSelectedOptions.forEach((optionId2, optionTypeId2) => {
            if (optionId2 === dependency) {
              isValid = true
            }
          })
        })

        if (!isValid) {
          // Find a similar option, or reset
          let newOption: ProductOptionStore | null = null
          for (const [, option2] of optionType.get('options')) {
            // Check for same slug
            if (option2.get('slug') !== option.get('slug')) {
              continue
            }

            // Verify dependency
            if (option2.get('listDependencies').count() > 0) {
              let isValid = false
              option2.get('listDependencies').forEach(dependency => {
                listSelectedOptions.forEach((optionId2, optionTypeId2) => {
                  if (optionId2 === dependency) {
                    isValid = true
                  }
                })
              })

              if (!isValid) continue
            }

            // Option is valid
            newOption = option2
          }

          if (newOption) {
            tmpSelectedOptions = tmpSelectedOptions.set(optionTypeId, newOption.get('id'))
          } else {
            tmpSelectedOptions = tmpSelectedOptions.remove(optionTypeId)
          }
        }
      }
    })

    setListSelectedOptions(tmpSelectedOptions)
  }, [listSelectedOptions])

  const modelOptions = useMemo(() => {
    let tmpModelOptions = Immutable.Map<string, string>()

    if (!productDesign) return tmpModelOptions

    //Add options
    listSelectedOptions.forEach((optionId, optionTypeId) => {
      const optionType = productDesign.get('availableOptionTypes').get(String(optionTypeId))
      const optionSlug = optionType?.get('options').get(String(optionId))?.get('slug')

      if (!optionType || !optionSlug) return

      tmpModelOptions = tmpModelOptions.set(optionType.get('slug'), optionSlug)
    })

    //Add details
    productDesign.get('details').forEach((valueSlug, detailSlug) => {
      tmpModelOptions = tmpModelOptions.set(detailSlug, valueSlug);
    })

    return tmpModelOptions
  }, [productDesign, listSelectedOptions, productData])

  // Calculate price
  const [price, discount] = useMemo(() => {
    // Default objects with the userCurrency to start
    let price = Dinero({
      amount: 0,
      currency: userCurrency
    })

    let discount = Dinero({
      amount: 0,
      currency: userCurrency
    })

    if (!productData || !productDesign) {
      return [price, discount]
    }

    // Base product price
    if (quantity >= 6) {
      price = productData.get('price_wholesale').toDinero()
    } else {
      price = productData.get('price_dropship').toDinero()
    }

    discount = productData.get('price_dropship').toDinero().subtract(productData.get('price_wholesale').toDinero())

    // Option prices
    listSelectedOptions.forEach((optionId, optionTypeId) => {
      const option = productDesign.get('availableOptionTypes').get(String(optionTypeId))?.get('options').get(String(optionId))

      if (!option) return

      if (quantity >= 6) {
        price = price.add(option.get('price_wholesale').toDinero())
      } else {
        price = price.add(option.get('price_dropship').toDinero())
      }

      discount = discount.add(option.get('price_dropship').toDinero().subtract(option.get('price_wholesale').toDinero()))
    })

    return [price, discount]
  }, [productData, productDesign, quantity, listSelectedOptions, userCurrency])

  const productionTime = useMemo(() => {
    if (!productData) return 0

    let tmpProductionTime = productData.get('production_time')

    // +3 days per 6 items
    tmpProductionTime += Math.max(0, Math.ceil((quantity - 6) / 6) * 3)

    // Max out at 30 days
    tmpProductionTime = Math.min(tmpProductionTime, 30)

    // Round to 1 digit
    tmpProductionTime = Math.round(tmpProductionTime * 10) / 10

    return tmpProductionTime
  }, [productData, quantity])

  const previewSize = useMemo(() => {
    const availablePreviewWidth = documentSize.width - (
      isMobile
        ? MOBILE_DIALOG_MARGIN + MOBILE_CONTENT_PADDING + 10
        : DESKTOP_DIALOG_MARGIN + DESKTOP_CONTENT_PADDING + DESKTOP_DETAILS_WIDTH + DESKTOP_CONTENT_GAP
    )

    let width = Math.max(0, Math.min(availablePreviewWidth, MAX_PREVIEW_WIDTH))
    let height = width * PREVIEW_RATIO

    if (!isMobile) {
      const maxBodyHeight = Math.max(MIN_DIALOG_BODY_HEIGHT, documentSize.height - DESKTOP_BODY_CHROME_HEIGHT)
      const maxPreviewHeight = maxBodyHeight - DESKTOP_CONTENT_PADDING - PREVIEW_THUMBNAIL_GAP - THUMBNAIL_LIST_HEIGHT

      if (height > maxPreviewHeight) {
        height = Math.max(0, maxPreviewHeight)
        width = height / PREVIEW_RATIO
      }
    }

    return {
      width,
      height
    }
  }, [documentSize.height, documentSize.width, isMobile])

  const orderDialogBodySize = useMemo(() => {
    const maxBodyHeight = Math.max(
      MIN_DIALOG_BODY_HEIGHT,
      documentSize.height - (isMobile ? MOBILE_BODY_CHROME_HEIGHT : DESKTOP_BODY_CHROME_HEIGHT)
    )

    if (isMobile) {
      return {
        height: maxBodyHeight,
        width: '100%'
      }
    }

    const orderBodyHeight = previewSize.height + DESKTOP_CONTENT_PADDING + PREVIEW_THUMBNAIL_GAP + THUMBNAIL_LIST_HEIGHT
    const orderBodyWidth = previewSize.width + DESKTOP_CONTENT_PADDING + DESKTOP_CONTENT_GAP + DESKTOP_DETAILS_WIDTH
    const maxBodyWidth = Math.max(MIN_DIALOG_BODY_WIDTH, documentSize.width - DESKTOP_DIALOG_MARGIN)

    return {
      height: Math.min(orderBodyHeight, maxBodyHeight),
      width: Math.min(orderBodyWidth, maxBodyWidth)
    }
  }, [documentSize.height, documentSize.width, isMobile, previewSize.height, previewSize.width])

  const isMissingOptions = useMemo(() => {
    if (!productDesign) {
      return false
    }

    let tmpIsMissingOptions = false
    productDesign.get('availableOptionTypes').forEach(optionType => {
      // Ignore bool options
      if (optionType.get('type') === 'bool') {
        return
      }

      // Skip hidden options
      if (!optionType.get('visible')) {
        return
      }

      const optionId = listSelectedOptions.get(optionType.get('id'))
      const option = optionType.get('options').get(String(optionId))

      if (!option || !isOptionDependencySatisfied(option, listSelectedOptions)) {
        tmpIsMissingOptions = true
      }
    })

    return tmpIsMissingOptions
  }, [listSelectedOptions, productDesign])

  useEffect(() => {
    if (!productData || !productDesign || !props.opened) return

    if (productData.get('viewerVersion').get('version') === 1) {
      // @ts-expect-error Legacy THREE
      window.THREE.Cache.enabled = true
      setTimeout(() => {
        window.aow.initUI('#order-preview-container')
      })
    }
  }, [props.opened, productData, selectedPreview, texture])

  useEffect(() => {
    if (!productDesign || !productData || productData.get('viewerVersion').get('version') !== 1) {
      return
    }
    // This is to support legacy ModelViewer
    listSelectedOptions.forEach((optionId, optionTypeId) => {
      const optionType = productDesign.get('availableOptionTypes').get(String(optionTypeId))
      if (!optionType) return

      $('#productInfo-productOption-' + optionType.get('slug')).trigger('change')
    })
  }, [listSelectedOptions, props.opened])

  const fabricVariant = useMemo(() => {
    if (!productDesign || productDesign.get('id_product') !== 50) return null

    let fabric: string | undefined
    let size: string | undefined

    for (const [, optionType] of productDesign.get('availableOptionTypes')) {
      if (optionType.get('slug') === 'fabric') {
        fabric = optionType.get('options').get(String(listSelectedOptions.get(optionType.get('id'))))?.get('slug')
      } else if (optionType.get('slug') === 'fabric-size') {
        size = optionType.get('options').get(String(listSelectedOptions.get(optionType.get('id'))))?.get('slug')
      }
    }

    return fabric && size ? fabric + '|' + size : null
  }, [productDesign, listSelectedOptions])

  const onConfirm = useCallback(() => {
    if (isMissingOptions) return

    props.onConfirm && props.onConfirm(listSelectedOptions, listSelectedBrandingOptions)
  }, [isMissingOptions, listSelectedOptions, listSelectedBrandingOptions, props.onConfirm])

  const optionString = useMemo(() => {
    if (!productDesign) return ''

    let optionString = ''
    let i = 0;
    listSelectedOptions.entrySeq().forEach(([optionTypeId, optionId]) => {
      const optionType = productDesign.get('availableOptionTypes').get(String(optionTypeId))
      const option = optionType?.get('options').get(String(optionId))

      if (!optionType || !option || ['include-label', 'printing-sides'].includes(optionType.get('slug'))) return ''

      optionString += (i > 0 ? ', ' : '') + option.get('name')
      i++;
    })
    return optionString
  }, [listSelectedOptions, productDesign])

  const onAddToCart = useCallback((cartId?: number, newCartName?: string, onSuccess?: () => void) => {
    if (!productDesign || isMissingOptions) return

    const fd = new FormData()

    if (cartId != null) {
      fd.append('cart_id', String(cartId))
    } else if (newCartName) {
      fd.append('cart_name', newCartName)
    }

    fd.append('quantity', String(quantity))
    fd.append('type', 'product')
    fd.append('product_id', String(productDesign.get('id_product')))
    fd.append('item_id', String(productDesign.get('id_product_design')))

    const option_ids:number[] = [];
    listSelectedOptions.forEach((optionId, optionTypeId) => {
      const optionType = productDesign.get('availableOptionTypes').get(String(optionTypeId))
      if (!optionType) return

      fd.append('options[' + optionType.get('slug') + ']', String(optionId))
      option_ids.push(optionId);
    })

    listSelectedBrandingOptions.forEach((brandingOptionId, optionId) => {
      fd.append('brandingOptions[' + optionId + ']', String(brandingOptionId))
    })

    const eventId = getMetaStandaloneEventId('AddToCart', 'product')
    appendMetaEventData(fd, eventId)

    dispatch(UpdateCartItem(fd).set({
      onSuccess: () => {
        setAddedToCart(true)
        onSuccess && onSuccess()
        
        const id = 'product-'+[productDesign.get('id_product'), ...option_ids.sort((a, b) => a - b)].join('-');
        const content_ids = [id];
        const itemPrice = price.toUnit();
        const contents: { id: string, quantity: number, item_price: number }[] = [{
          id: id,
          quantity: quantity,
          item_price: itemPrice,
        }]
        const totalPrice = price.multiply(quantity);

        trackMetaEvent('AddToCart', {
          content_type: 'product',
          content_ids: content_ids,
          contents: contents,
          currency: totalPrice.getCurrency(),
          value: totalPrice.toUnit(),
        }, eventId)
        const productName = productData?.get('name')
        trackOpenAIAdsItemsAdded(totalPrice, [{
          id,
          name: typeof productName === 'string' ? productName : undefined,
          content_type: 'product',
          quantity,
          amount: price.getAmount(),
          currency: totalPrice.getCurrency()
        }], eventId)
      }
    }))
  }, [quantity, productDesign, productData, isMissingOptions, listSelectedOptions, listSelectedBrandingOptions, price])

  const renderTexture = useCallback(() => {
    return texture !== null ? texture : undefined
  }, [texture])

  const variant = useMemo(() => {
    return productData?.get('variants').get(String(productDesign?.get('id_product_variant')))?.get('slug') ?? 'default'
  }, [productData, productDesign?.get('id_product_variant')])

  const labData = useMemo(() => {
    const fullLabData = productData?.get('labData');

    if (!fullLabData) return undefined;

    if (fullLabData.has_subproducts) {
      return subproduct ? fullLabData.subproducts[subproduct]?.originalData : undefined;
    }

    return fullLabData.originalData;
  }, [productData, subproduct])

  if (!productDesign || !productData) {
    return <StyledDialogContent>
      <Box
        component="div"
        sx={{
          py: 4,
          px: 8,
          minHeight: '200px',
          minWidth: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {error ? <Stack spacing={2} alignItems="center">
          <Alert
            severity="error"
          >{t('An error occurred while loading the product.')}</Alert>
          <Button
            variant="outlined"
            color="secondary"
            onClick={props.onClose}
          >{t('Close')}</Button>
        </Stack> : <LabLoader />}
      </Box>
    </StyledDialogContent>
  }

  return <>
    {showTabs ? <OrderDialogTabsHeader
      value={selectedTab}
      onChange={setSelectedTab}
    /> : null}
    <OrderDialogTabsBody style={orderDialogBodySize}>
      <OrderDialogOrderPanel showTabs={showTabs} selectedTab={selectedTab} tab="order">
      <StyledDialogContent>
      <PreviewListWrapper style={{
        width: previewSize.width
      }}>
        <div style={{
          overflow: 'hidden',
          borderRadius: 20,
          height: previewSize.height,
          position: 'relative',
          backgroundColor: '#ededed'
        }}>
          {productData.get('viewerVersion').get('version') === 1 || texture ? <Fade
            in={selectedPreview === 0}
          >
            <PreviewWrapper className={productDesign.get('id_product') !== 50 ? 'is-3d' : ''} style={{ width: previewSize.width, height: previewSize.height }}>
              {
                productDesign.get('id_product') === 50 ? <FabricViewerWrapper style={{ width: previewSize.width - 10 }}>
                  {fabricVariant && labData ? <FabricViewer
                    labData={labData}
                    variant={fabricVariant}
                    textureUrl={textureUrl}
                    inLab={false}
                    options={modelOptions}
                  /> : null}
                </FabricViewerWrapper> : (productData.get('viewerVersion').get('version') === 1 ? <div id="order-preview-container" style={{ width: '100%', height: '100%' }}>
                  {productData.get('has_subproducts') ? <div
                    className="image-gallery-3dmodelcontainer"
                    data-product-variant={variant !== 'default' ? variant : undefined}
                    data-product-slug={productData.get('slug')}
                    data-product-id={productData.get('id')}
                    data-textures={textureUrls}
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      overflow: 'hidden'
                    }}
                  ></div> : (textureUrl ? <div
                    className="image-gallery-3dmodel"
                    data-product-variant={variant !== 'default' ? variant : undefined}
                    data-product-slug={productData.get('slug')}
                    data-product-id={productData.get('id')}
                    data-texture={textureUrl}
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      overflow: 'hidden'
                    }}
                  ></div> : null)}

                  {productDesign.get('availableOptionTypes').valueSeq().map(optionType => {
                    // Default to first option if none are selected so we don't break the models
                    const option = listSelectedOptions.get(optionType.get('id')) !== undefined ? optionType.get('options').get(String(listSelectedOptions.get(optionType.get('id')))) : optionType.get('options').first()
                    if (!option) return null

                    return <select
                      key={optionType.get('id')}
                      id={'productInfo-productOption-' + optionType.get('slug')}
                      value={option.get('id')}
                      className="browser-default"
                      style={{ display: 'none' }}
                      onChange={() => { }}
                    >
                      <option value={option.get('id')} data-slug={option.get('slug')}></option>
                    </select>
                  })}
                </div> : (productData.get('viewerVersion').get('version') === 2 && texture ? <Suspense fallback={<LabLoader />}>
                  <NoSsr>
                    <ErrorBoundary fallback={<Alert
                      severity="error"
                    >{t('An error occurred while loading the 3D preview.')}</Alert>}>
                      {labData ? <ModelViewer
                        renderTexture={renderTexture}
                        labData={labData}
                        productId={productDesign.get('id_product')}
                        options={modelOptions}
                        variant={variant}
                        viewerMode="website"
                      /> : null}
                    </ErrorBoundary>
                  </NoSsr>
                </Suspense>
                  : <></>)
                )
              }

            </PreviewWrapper>
          </Fade> : null}

          {productDesign.get('previews').valueSeq().map(preview => {
            return <Fade
              key={preview.get('id')}
              in={selectedPreview === preview.get('id')}
            >
              <img
                src={preview.get('url')}
                width={previewSize.width}
                height={previewSize.height}
                style={{
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  left: 0
                }}
              />
            </Fade>
          })}
        </div>
        <ThumbnailList>
          {textureUrl ? <Thumbnail
            className={selectedPreview === 0 ? 'active' : ''}
            onClick={() => setSelectedPreview(0)}
          >
            <ThumbnailImg src={productDesign.get('preview')} />
            <ThumbnailIconWrapper>
              <ViewInArIcon style={{
                width: '70%',
                height: '70%',
                color: '#fff'
              }} />
            </ThumbnailIconWrapper>
          </Thumbnail> : null}
          {productDesign.get('previews').sort((a, b) => a.get('theorder') - b.get('theorder')).valueSeq().map(preview => {
            return <Thumbnail
              key={preview.get('id')}
              onClick={() => setSelectedPreview(preview.get('id'))}
              className={selectedPreview === preview.get('id') ? 'active' : ''}
            >
              <ThumbnailImg src={preview.get('url')} />
            </Thumbnail>
          })}
        </ThumbnailList>
      </PreviewListWrapper>

      <DetailsWrapper>
        <Typography
          variant="h2"
          style={{
            fontSize: '36px'
          }}
        >{productDesign.get('name')}</Typography>
        <Typography
          variant="body1"
          style={{
            marginBottom: 20
          }}
        >{productData.get('name')}</Typography>

        <Collapse in={addedToCart}>
          <Stack spacing={2}>
            <Alert
              severity="success"
            ><AlertTitle>{t('Added to cart')}</AlertTitle>
              {String(quantity) + 'x ' + productData.get('name')}<br />
              {(optionString !== '' ? '(' + optionString + ')' : '')}
            </Alert>
            <Stack
              direction="row"
              spacing={2}
            >
              <Button
                variant="contained"
                color="accent"
                component={Link}
                to={tR('/cart')}
                reloadDocument={props.cartLinkReloadDocument}
              >{t('Go to Cart')}</Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setAddedToCart(false)}
              >{t('Order more')}</Button>
            </Stack>
          </Stack>
        </Collapse>
        <Collapse in={!addedToCart}>
          <Stack spacing={2}>
            {productDesign.get('availableOptionTypes').sort((a, b) => a.get('theorder') - b.get('theorder')).valueSeq().map(optionType => {
              // Skip hidden options
              if (!optionType.get('visible')) {
                return null
              }

              let value: number | undefined = listSelectedOptions.get(optionType.get('id'))

              // Default to empty if it's invalid
              if (!optionType.get('options').has(String(value))) {
                value = undefined
              }

              if (optionType.get('type') === 'bool') {
                const singleOption = optionType.get('options').first()
                if (!singleOption) return null

                const price = (quantity >= 6 ? singleOption.get('price_wholesale') : singleOption.get('price_dropship')).toDinero()

                // TODO: Hardcoded until we have a proper way to identify branding option types
                if (optionType.get('slug') === 'include-label') {
                  const selectedId = listSelectedBrandingOptions.get(singleOption.get('id'))
                  return <div key={optionType.get('id')}>
                    <ProductBrandingOption
                      typeId={1}
                      value={selectedId}
                      onChange={(idBrandingOption) => {
                        setListSelectedOptions(value => idBrandingOption !== undefined
                          ? value.set(optionType.get('id'), singleOption.get('id'))
                          : value.remove(optionType.get('id'))
                        )
                        setListSelectedBrandingOptions(value => idBrandingOption !== undefined
                          ? value.set(singleOption.get('id'), idBrandingOption)
                          : value.remove(singleOption.get('id'))
                        )
                      }}
                    />
                  </div>
                } else {
                  return <div key={optionType.get('id')}>
                    <FormControlLabel
                      key={optionType.get('id')}
                      control={<Checkbox
                        checked={listSelectedOptions.get(optionType.get('id')) === singleOption.get('id')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setListSelectedOptions(listSelectedOptions.set(optionType.get('id'), singleOption.get('id')))
                          } else {
                            setListSelectedOptions(listSelectedOptions.remove(optionType.get('id')))
                          }
                        }}
                      />}
                      label={optionType.get('name') + (!price.isZero() ? ' (' + price.toFormat() + ')' : '')}
                    />
                  </div>
                }
              } else {
                const availableOptions = getAvailableOptions(optionType, listSelectedOptions)

                const optionLabel = (option: ProductOptionStore) => {
                  const price = (quantity >= 6 ? option.get('price_wholesale') : option.get('price_dropship')).toDinero()

                  return option.get('name') + (!price.isZero() ? ' (' + price.toFormat() + ')' : '')
                }

                if (availableOptions.length > 0 && availableOptions.every(option => option.get('color') !== null)) {
                  return <ColorField
                    key={optionType.get('id')}
                    label={optionType.get('name')}
                    value={value ?? ''}
                    onChange={(optionId) => {
                      setListSelectedOptions(listSelectedOptions.set(optionType.get('id'), Number(optionId)))
                    }}
                    options={availableOptions.map(option => ({
                      value: option.get('id'),
                      label: optionLabel(option),
                      color: option.get('color') ?? '#ffffff',
                    }))}
                  />
                }

                return <SelectV2
                  key={optionType.get('id')}
                  label={optionType.get('name')}
                  value={value ?? ''}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setListSelectedOptions(listSelectedOptions.set(optionType.get('id'), Number(e.target.value)))
                  }}
                >
                  {availableOptions.map(option => {
                    return <MenuItem
                      key={option.get('id')}
                      value={option.get('id')}
                    >{optionLabel(option)}</MenuItem>
                  })}
                </SelectV2>
              }
            })}

            <div style={{
              position: 'relative'
            }}>
              <div style={{
                filter: isMissingOptions ? 'blur(5px)' : ''
              }}>
                {props.onConfirm ? <Stack spacing={2} alignItems="flex-start">
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{
                      position: 'relative'
                    }}>
                      {quantity >= 6 && !discount.isZero() && discount.isPositive() ? <div style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        textDecoration: 'line-through',
                        color: '#999',
                        position: 'absolute',
                        top: '-12px'
                      }}>{price.add(discount).toFormat()} </div> : null}
                      <Price>{price.toFormat()} </Price>
                    </div>
                  </div>
                  <Button
                    variant="contained"
                    color="accent"
                    disabled={isMissingOptions}
                    onClick={onConfirm}
                  >{t('Confirm')}</Button>
                </Stack> : <>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <InputLabel
                      htmlFor="order-quantity"
                    >{t('Quantity')}</InputLabel>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <NumberFieldV2
                        id="order-quantity"
                        value={quantity}
                        onChange={setQuantity}
                        min={1}
                        max={99999}
                      />
                      <div style={{
                        margin: '0 5px'
                      }}><CloseIcon fontSize="small" style={{ display: 'block' }} /></div>
                      <div style={{
                        position: 'relative'
                      }}>
                        {quantity >= 6 && !discount.isZero() && discount.isPositive() ? <div style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          textDecoration: 'line-through',
                          color: '#999',
                          position: 'absolute',
                          top: '-12px'
                        }}>{price.add(discount).toFormat()} </div> : null}
                        <Price>{price.toFormat()} </Price>
                      </div>
                    </div>
                  </div>

                  {quantity < 6 && !discount.isZero() && discount.isPositive()
                    ? <WholesaleNotice>{t('Add {{count}} more to get {{discount}} off each!', { count: (6 - quantity), discount: discount.toFormat() })}</WholesaleNotice>
                    : null
                  }

                  <div style={{
                    fontSize: '14px',
                    marginTop: '10px',
                    marginBottom: '20px'
                  }}>{t('Production Time: ')}<strong>~{t('{{productionTime}} business days', { productionTime })}</strong></div>

                  <AddToCartButton onAdd={onAddToCart} />
                </>}
              </div>

              {isMissingOptions ? <div style={{
                position: 'absolute',
                top: '0px',
                left: '0px',
                right: '0px',
                bottom: '0px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 500,
                gap: '10px'
              }}>{t('Select your options')} <ArrowUpwardIcon fontSize="small" /></div> : null}
            </div>
          </Stack>
        </Collapse>
      </DetailsWrapper>
      </StyledDialogContent>
      </OrderDialogOrderPanel>
      {showTabs ? <>
      <OrderDialogTabPanel selectedTab={selectedTab} tab="materials">
        <ProductMaterialsTab
          idProduct={productData.get('id')}
        />
      </OrderDialogTabPanel>
      <OrderDialogTabPanel selectedTab={selectedTab} tab="gpsr">
        <ProductGpsrTab
          idProduct={productData.get('id')}
        />
      </OrderDialogTabPanel>
      </> : null}
    </OrderDialogTabsBody>
  </>
}
